import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { convertDataToISO, maskData } from "@/lib/utils";
import { Especialidade } from "@/pages/especialidades/interface/Especialidade.interface";
import { useQuery } from "@tanstack/react-query";
import { FileKey, Loader2, Upload } from "lucide-react";
import { useCallback, useRef, useState } from "react";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface MedicoProps {
  medicoData: {
    crm: string;
    uf_crm: string;
    id_especialidade: string;
    subespecialidades?: string;
    status_crm: "ATIVO" | "INATIVO" | "SUSPENSO";
    data_validade_crm?: string;
    observacoes?: string;
    ativo: boolean;
  };
  setMedicoData: (data: any) => void;
  especialidades: Especialidade[];
  readOnly?: boolean;
  /** ID do usuário (médico) sendo editado. Quando definido, exibe upload e status do certificado A1. */
  usuarioId?: number;
}

function formatarDataValidade(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return [d, m, y].filter(Boolean).join("/") || iso;
}

const UFS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

export const Medico = ({ medicoData, setMedicoData, especialidades, readOnly = false, usuarioId }: MedicoProps) => {
  const [dataValidacaoErro, setDataValidacaoErro] = useState<string>("");
  const [crmErro, setCrmErro] = useState<string>("");
  const [verificandoCrm, setVerificandoCrm] = useState(false);
  const [uploadCertificadoPending, setUploadCertificadoPending] = useState(false);
  const [senhaCertificado, setSenhaCertificado] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const statusCertificado = useQuery({
    queryKey: ["certificado-a1-status", usuarioId],
    queryFn: async () => {
      if (!usuarioId) return null;
      const url = `/api/certificado-a1/usuarios/${usuarioId}/status`;
      const res = (await api.get(url)) as { result?: { possuiCertificado: boolean; validoAte: string | null } };
      return res.result ?? null;
    },
    enabled: !!usuarioId,
  });

  const uploadCertificado = useCallback(
    async (file: File, senha: string) => {
      if (!usuarioId || !senha.trim()) {
        toast({ title: "Erro", description: "Informe a senha do certificado.", variant: "destructive" });
        return;
      }
      setUploadCertificadoPending(true);
      try {
        const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
        const formData = new FormData();
        formData.append("file", file);
        formData.append("senha", senha);
        const res = await fetch(`${BASE_URL}/api/certificado-a1/usuarios/${usuarioId}/upload`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message ?? data?.error ?? "Erro ao enviar certificado");
        toast({ title: "Sucesso", description: "Certificado digital cadastrado com sucesso." });
        setSenhaCertificado("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        await statusCertificado.refetch();
      } catch (err: any) {
        toast({ title: "Erro", description: err?.message ?? "Não foi possível cadastrar o certificado.", variant: "destructive" });
      } finally {
        setUploadCertificadoPending(false);
      }
    },
    [usuarioId, statusCertificado]
  );

  const handleFileCertificado = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const lower = file.name.toLowerCase();
      if (!lower.endsWith(".pfx") && !lower.endsWith(".p12")) {
        toast({ title: "Formato inválido", description: "Use arquivo .pfx ou .p12", variant: "destructive" });
        return;
      }
      if (senhaCertificado.trim()) {
        uploadCertificado(file, senhaCertificado.trim());
      } else {
        const s = window.prompt("Digite a senha do certificado:");
        if (s != null) uploadCertificado(file, s);
      }
      e.target.value = "";
    },
    [senhaCertificado, uploadCertificado]
  );

  const verificarCrm = async (crm: string) => {
    if (!crm || crm.trim() === "") {
      setCrmErro("");
      return;
    }

    setVerificandoCrm(true);
    setCrmErro("");

    try {
      const response = await api.post(`/api/usuario/verificar-crm`, {
        crm: crm
      });

      if (!response.result?.disponivel) {
        setCrmErro("Este CRM já está cadastrado no sistema");
      }
    } catch (error: any) {
      console.error("Erro ao verificar CRM:", error);
      setCrmErro("Erro ao verificar disponibilidade");
    } finally {
      setVerificandoCrm(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados do Médico</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="crm">CRM</Label>
            <Input
              id="crm"
              value={medicoData.crm}
              className={crmErro ? "border-red-500" : ""}
              disabled={verificandoCrm || readOnly}
              readOnly={readOnly}
              onChange={(e) => {
                setMedicoData({ ...medicoData, crm: e.target.value });
                setCrmErro("");
              }}
              onBlur={(e) => {
                verificarCrm(e.target.value);
              }}
              placeholder="000000"
              maxLength={20}
            />
            {crmErro && (
              <p className="text-sm text-red-500">{crmErro}</p>
            )}
            {verificandoCrm && (
              <p className="text-sm text-muted-foreground">Verificando disponibilidade...</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="uf_crm">UF do CRM</Label>
            <Select
              value={medicoData.uf_crm}
              onValueChange={(value) => setMedicoData({ ...medicoData, uf_crm: value })}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a UF" />
              </SelectTrigger>
              <SelectContent>
                {UFS_BRASIL.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="id_especialidade">
            Especialidade <span className="text-red-500">*</span>
          </Label>
          <Select
            value={medicoData.id_especialidade}
            onValueChange={(value) => setMedicoData({ ...medicoData, id_especialidade: value })}
            required
            disabled={readOnly}
          >
            <SelectTrigger className={!medicoData.id_especialidade ? "border-red-500" : ""}>
              <SelectValue placeholder="Selecione a especialidade" />
            </SelectTrigger>
            <SelectContent>
              {especialidades.map((especialidade) => (
                <SelectItem key={especialidade.id} value={especialidade.id.toString()}>
                  {especialidade.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!medicoData.id_especialidade && (
            <p className="text-sm text-red-500">Especialidade é obrigatória para médicos</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="subespecialidades">Subespecialidades</Label>
          <Input
            id="subespecialidades"
            value={medicoData.subespecialidades || ""}
            onChange={(e) => setMedicoData({ ...medicoData, subespecialidades: e.target.value })}
            placeholder="Ex: Retina, Glaucoma, etc."
            maxLength={200}
            disabled={readOnly}
            readOnly={readOnly}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status_crm">Status do CRM</Label>
            <Select
              value={medicoData.status_crm}
              onValueChange={(value: "ATIVO" | "INATIVO" | "SUSPENSO") => 
                setMedicoData({ ...medicoData, status_crm: value })
              }
              required
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ATIVO">Ativo</SelectItem>
                <SelectItem value="INATIVO">Inativo</SelectItem>
                <SelectItem value="SUSPENSO">Suspenso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="data_validade_crm">Data de Validade do CRM</Label>
            <Input
              id="data_validade_crm"
              type="text"
              placeholder="dd/MM/yyyy (ex: 31/12/2025)"
              maxLength={10}
              value={medicoData.data_validade_crm || ""}
              className={dataValidacaoErro ? "border-red-500" : ""}
              disabled={readOnly}
              readOnly={readOnly}
              onChange={(e) => {
                const valor = e.target.value;
                // Limpa o erro quando o usuário começa a digitar
                setDataValidacaoErro("");
                // Aplica a máscara enquanto o usuário digita
                const valorFormatado = maskData(valor);
                setMedicoData({ ...medicoData, data_validade_crm: valorFormatado });
              }}
              onBlur={(e) => {
                // Valida a data quando o campo perde o foco
                const valor = e.target.value.trim();
                
                // Se o campo estiver vazio, limpa o erro e retorna
                if (!valor || valor.length === 0) {
                  setDataValidacaoErro("");
                  return;
                }
                
                // Se a data não estiver completa, mostra erro
                if (valor.length < 10) {
                  setDataValidacaoErro("Data incompleta. Use o formato dd/MM/yyyy");
                  return;
                }
                
                // Valida se a data é válida usando a função de conversão
                const dataISO = convertDataToISO(valor);
                if (!dataISO) {
                  setDataValidacaoErro("Data inválida. Exemplo: 31/12/2025");
                } else {
                  setDataValidacaoErro("");
                }
              }}
            />
            {dataValidacaoErro && (
              <p className="text-sm text-red-500">{dataValidacaoErro}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="observacoes">Observações</Label>
          <Textarea
            id="observacoes"
            value={medicoData.observacoes || ""}
            onChange={(e) => setMedicoData({ ...medicoData, observacoes: e.target.value })}
            placeholder="Observações sobre o médico..."
            rows={4}
            disabled={readOnly}
            readOnly={readOnly}
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="medico_ativo"
            checked={medicoData.ativo}
            onChange={(e) => setMedicoData({ ...medicoData, ativo: e.target.checked })}
            className="rounded"
            disabled={readOnly}
          />
          <Label htmlFor="medico_ativo">Médico ativo</Label>
        </div>

        {/* Certificado digital A1 */}
        <div className="border-t pt-4 mt-4 space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <FileKey className="h-4 w-4" />
            Certificado digital A1
          </h4>
          {!usuarioId ? (
            <p className="text-sm text-muted-foreground">
              Salve o usuário primeiro para cadastrar o certificado digital (.pfx ou .p12) para assinatura de documentos.
            </p>
          ) : (
            <>
              {statusCertificado.isLoading && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando status...
                </p>
              )}
              {statusCertificado.data?.possuiCertificado && (
                <p className="text-sm text-success">
                  Certificado ativo
                  {statusCertificado.data.validoAte
                    ? ` (válido até ${formatarDataValidade(statusCertificado.data.validoAte)})`
                    : ""}
                </p>
              )}
              {!readOnly && (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="senha_certificado">Senha do certificado</Label>
                    <Input
                      id="senha_certificado"
                      type="password"
                      placeholder="Senha do .pfx/.p12"
                      value={senhaCertificado}
                      onChange={(e) => setSenhaCertificado(e.target.value)}
                      className="max-w-xs"
                    />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pfx,.p12"
                    className="hidden"
                    onChange={handleFileCertificado}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadCertificadoPending}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 disabled:opacity-50"
                  >
                    {uploadCertificadoPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {uploadCertificadoPending ? "Enviando..." : "Enviar certificado"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

